// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/elements/Modal';
import {
  FiUploadCloud,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiCalendar,
  FiCpu,
  FiAlertTriangle,
} from 'react-icons/fi';
import { LuRefrigerator } from 'react-icons/lu';
import {
  APP_BG,
  APP_BORDER,
  APP_TEXT,
  APP_SEMANTIC,
} from '@/constant/theme/colors.constant';
import {
  FETCH_LOG_OPTIONS,
  FetchLogRange,
} from '@/constant/smartCooler/smartCooler.constant';
import { tid } from '@/utils/testId';
import { SMART_COOLER_TEST_IDS as TID } from '@/constant/testIds/smartCooler.testIds';
import { ISmartCooler } from './smartCooler.interface';

interface FetchLogModalProps {
  open: boolean;
  item: ISmartCooler | null;
  onClose: () => void;
  onDoneClose?: () => void;
  onSubmit?: (item: ISmartCooler, range: FetchLogRange) => void;
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  actionMessage?: string | null;
  actionData?: { actionId?: string; status?: string } | null;
  actionError?: string | null;
}

// ─── Range card icon & accent per option ─────────────────────────────────────
const RANGE_META: Record<
  FetchLogRange,
  { icon: React.ReactNode; accent: string; glow: string; ringColor: string }
> = {
  TODAY: {
    icon: <FiClock size={18} />,
    accent: 'from-primary/20 to-primary/5 border-primary/40',
    glow: 'shadow-primary/20',
    ringColor: 'ring-primary/40',
  },
  LAST_2_DAYS: {
    icon: <FiCalendar size={18} />,
    accent: 'from-primary/20 to-primary/5 border-primary/40',
    glow: 'shadow-primary/20',
    ringColor: 'ring-primary/40',
  },
  LAST_3_DAYS: {
    icon: <FiCalendar size={18} />,
    accent: 'from-primary/20 to-primary/5 border-primary/40',
    glow: 'shadow-primary/20',
    ringColor: 'ring-primary/40',
  },
};

const FetchLogModal: React.FC<FetchLogModalProps> = ({
  open,
  item,
  onClose,
  onDoneClose,
  onSubmit,
  isLoading = false,
  isSuccess = false,
  isError = false,
  actionMessage,
  actionData,
  actionError,
}) => {
  const [selectedRange, setSelectedRange] = useState<FetchLogRange>('TODAY');

  useEffect(() => {
    if (open) setSelectedRange('TODAY');
  }, [open]);

  const handleSubmit = () => {
    if (!item) return;
    onSubmit?.(item, selectedRange);
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const isDone = isSuccess || isError;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      variant={isError ? 'danger' : isSuccess ? 'success' : 'default'}
      size="xl"
    >
      <Modal.Header
        title="Device Log Upload"
        subtitle="Select a time window and initiate log collection"
        icon={<FiUploadCloud className="w-4 h-4" />}
        onClose={handleClose}
      />

      <Modal.Body>
        {isDone ? (
          /* ── Result state ─────────────────────────────────────── */
          <div className="flex flex-col items-center gap-4 py-4">
            {isSuccess ? (
              <>
                {/* Success icon */}
                <div className="relative flex items-center justify-center w-16 h-16">
                  <div
                    className="absolute inset-0 rounded-full bg-emerald-500/10 animate-ping"
                    style={{ animationDuration: '2.5s' }}
                  />
                  <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/25 ring-2 ring-emerald-400/30">
                    <FiCheckCircle size={30} className="text-emerald-500" />
                  </div>
                </div>

                <div className="text-center">
                  <p className={`text-base font-bold ${APP_TEXT.soft}`}>
                    Request Dispatched
                  </p>
                  <p className={`text-xs mt-1 ${APP_TEXT.muted}`}>
                    {actionMessage ||
                      `Log upload initiated for ${item?.nativeId}.`}
                  </p>
                </div>

                {/* Action ID card */}
                {actionData?.actionId && (
                  <div
                    className={`w-full rounded-xl border ${APP_BORDER.soft} ${APP_BG.surface} overflow-hidden`}
                  >
                    <div
                      className={`px-3 py-1.5 border-b ${APP_BORDER.soft} ${APP_BG.elevated}`}
                    >
                      <span
                        className={`text-[10px] font-bold uppercase tracking-widest ${APP_TEXT.muted}`}
                      >
                        Action Receipt
                      </span>
                    </div>
                    <div className="px-3 py-2 flex flex-col gap-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[11px] ${APP_TEXT.muted}`}>
                          Action ID
                        </span>
                        <span
                          className={`text-[11px] font-mono font-medium ${APP_TEXT.soft} truncate max-w-[180px]`}
                        >
                          {actionData.actionId}
                        </span>
                      </div>
                      {actionData.status && (
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-[11px] ${APP_TEXT.muted}`}>
                            Status
                          </span>
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-500">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            {actionData.status}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Error icon */}
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 ring-2 ring-red-400/30">
                  <FiXCircle size={30} className="text-red-500" />
                </div>
                <div className="text-center">
                  <p className={`text-base font-bold ${APP_TEXT.soft}`}>
                    Upload Failed
                  </p>
                  <p className={`text-xs mt-1 ${APP_TEXT.muted}`}>
                    {actionError || 'Something went wrong. Please try again.'}
                  </p>
                </div>
                {/* Error hint */}
                <div
                  className={`w-full flex items-start gap-2 rounded-xl px-3 py-2.5 ${APP_SEMANTIC.danger.bg} border ${APP_SEMANTIC.danger.border}`}
                >
                  <FiAlertTriangle
                    size={14}
                    className={`mt-0.5 flex-shrink-0 ${APP_SEMANTIC.danger.text}`}
                  />
                  <p
                    className={`text-[11px] leading-relaxed ${APP_SEMANTIC.danger.text}`}
                  >
                    Check the device connection status and retry. If the problem
                    persists, contact support.
                  </p>
                </div>
              </>
            )}
          </div>
        ) : (
          /* ── Selection state ──────────────────────────────────── */
          <div className="flex flex-col gap-4">
            {/* Device identity card */}
            <div
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border ${APP_BORDER.soft} ${APP_BG.surface}`}
            >
              <div
                className={`flex items-center justify-center w-9 h-9 rounded-lg ${APP_SEMANTIC.info.bg} border ${APP_SEMANTIC.info.border} flex-shrink-0`}
              >
                <LuRefrigerator size={16} className={APP_SEMANTIC.info.text} />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-[13px] font-bold truncate ${APP_TEXT.soft}`}
                >
                  {item?.nativeId || '—'}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-mono ${APP_TEXT.muted}`}
                  >
                    <FiCpu size={10} />
                    {item?.deviceId || '—'}
                  </span>
                  {item?.status && (
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold ${item.status === 'CONNECTED' ? 'text-emerald-500' : 'text-red-400'}`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${item.status === 'CONNECTED' ? 'bg-emerald-500' : 'bg-red-400'}`}
                      />
                      {item.status}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Range selector label */}
            <div className="flex items-center gap-2">
              <span
                className={`text-[10px] font-bold uppercase tracking-widest ${APP_TEXT.muted}`}
              >
                Log Time Window
              </span>
              <div className={`flex-1 h-px ${APP_BG.muted}`} />
            </div>

            {/* Entity-style range cards */}
            <div className="grid grid-cols-3 gap-2">
              {FETCH_LOG_OPTIONS.map((opt) => {
                const isSelected = selectedRange === opt.id;
                const meta = RANGE_META[opt.id as FetchLogRange];
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={
                      opt.id === 'TODAY'
                        ? tid(TID.FETCH_LOG_RANGE_TODAY)
                        : opt.id === 'LAST_2_DAYS'
                          ? tid(TID.FETCH_LOG_RANGE_2_DAYS)
                          : tid(TID.FETCH_LOG_RANGE_3_DAYS)
                    }
                    disabled={isLoading}
                    onClick={() => !isLoading && setSelectedRange(opt.id)}
                    className={`
                                            relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 text-center
                                            transition-all duration-200 outline-none focus-visible:ring-2
                                            ${isLoading ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                                            ${
                                              isSelected
                                                ? `bg-gradient-to-b ${meta.accent} shadow-lg ${meta.glow} ${meta.ringColor} scale-[1.03]`
                                                : `${APP_BORDER.default} ${APP_BG.base} hover:${APP_BG.surface} hover:border-gray-300 dark:hover:border-gray-600`
                                            }
                                        `}
                  >
                    {/* Selected indicator dot */}
                    {isSelected && (
                      <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary shadow-sm shadow-primary/50" />
                    )}

                    {/* Icon */}
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors
                                            ${
                                              isSelected
                                                ? 'bg-primary/15 text-primary'
                                                : `${APP_BG.surface} ${APP_TEXT.subtle}`
                                            }`}
                    >
                      {meta.icon}
                    </div>

                    {/* Label */}
                    <div>
                      <p
                        className={`text-[12px] font-bold leading-tight ${isSelected ? APP_TEXT.primary : APP_TEXT.soft}`}
                      >
                        {opt.label}
                      </p>
                      <p
                        className={`text-[10px] mt-0.5 leading-snug ${APP_TEXT.muted}`}
                      >
                        {opt.description
                          .replace('Upload logs ', '')
                          .replace('Upload ', '')}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Info note */}
            <p
              className={`text-[10px] leading-relaxed ${APP_TEXT.subtle} text-center`}
            >
              Logs are queued on the device and uploaded to the server
              asynchronously.
            </p>
          </div>
        )}
      </Modal.Body>

      {isDone ? (
        <Modal.Footer>
          <Modal.ConfirmButton
            testId={tid(TID.FETCH_LOG_DONE_BTN)}
            onClick={onDoneClose ?? onClose}
          >
            Done
          </Modal.ConfirmButton>
        </Modal.Footer>
      ) : (
        <Modal.Footer>
          <Modal.CancelButton
            testId={tid(TID.FETCH_LOG_CANCEL_BTN)}
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Modal.CancelButton>
          <Modal.ConfirmButton
            testId={tid(TID.FETCH_LOG_SUBMIT_BTN)}
            onClick={handleSubmit}
            disabled={isLoading}
            loading={isLoading}
            icon={<FiUploadCloud size={14} />}
          >
            Upload Logs
          </Modal.ConfirmButton>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default FetchLogModal;
